import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../utils/supabase';
import { resetPassword } from '../services/authService';
import { PASSWORD_RESET_EMAIL_SENT_MESSAGE } from '../utils/passwordReset';
import Button from '../components/common/Button';
import { BackButton } from '../components/common';
import { Heading, Text, Typography } from '../components/common/Typography';
import { Card } from '../components/common/Card';
import { FormSection, FormField, FormGrid, Input } from '../components/common/Form';
import ConfirmationModal from '../components/common/ConfirmationModal';
import { Modal, ModalContent } from '../components/common';
import ImageCropModal from '../components/image/ImageCropModal';
import { uploadPlayerAvatar, removePlayerAvatar, getPlayerAvatarPublicUrl } from '../services/avatarService';
import { COMPETITION_CATEGORY_LABELS } from '../utils/validation';

const AVATAR_ACCEPT = 'image/jpeg,image/jpg,image/png,image/webp';
const AVATAR_MAX_BYTES = 5 * 1024 * 1024; // 5MB

const AccountSettings = ({ currentUser, onUserUpdate, embedded = false }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Profile information state
  const [profileData, setProfileData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    gender: ''
  });
  
  // Password reset state
  const [showPasswordResetModal, setShowPasswordResetModal] = useState(false);
  
  // Email change state
  const [showEmailChangeModal, setShowEmailChangeModal] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  
  // Modal states
  const [showGenderWarning, setShowGenderWarning] = useState(false);
  const [showGenderConfirmation, setShowGenderConfirmation] = useState(false);
  const [pendingGenderChange, setPendingGenderChange] = useState('');
  const [originalGender, setOriginalGender] = useState('');

  // Avatar upload state
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [removingAvatar, setRemovingAvatar] = useState(false);
  const [avatarError, setAvatarError] = useState(null);
  const [avatarSuccess, setAvatarSuccess] = useState(null);
  const [avatarCropModalOpen, setAvatarCropModalOpen] = useState(false);
  const [selectedAvatarFile, setSelectedAvatarFile] = useState(null);
  const [avatarCropImageUrl, setAvatarCropImageUrl] = useState(null);

  // Load user profile data
  useEffect(() => {
    if (currentUser) {
      const userGender = currentUser.gender || '';
      setOriginalGender(userGender);
      setProfileData({
        first_name: currentUser.first_name || '',
        last_name: currentUser.last_name || '',
        email: currentUser.email || '',
        gender: userGender
      });
    }
  }, [currentUser]);

  // Handle gender change with confirmation
  const handleGenderChange = (newGender) => {
    // If changing from original gender, show confirmation modal
    if (originalGender && originalGender !== newGender && newGender !== '') {
      setPendingGenderChange(newGender);
      setShowGenderConfirmation(true);
    } else {
      // Direct change if no original gender or reverting to original
      setProfileData({ ...profileData, gender: newGender });
      setShowGenderWarning(false);
    }
  };

  // Confirm gender change
  const confirmGenderChange = () => {
    setProfileData({ ...profileData, gender: pendingGenderChange });
    setShowGenderConfirmation(false);
    setPendingGenderChange('');
    setShowGenderWarning(true);
  };

  // Cancel gender change
  const cancelGenderChange = () => {
    setShowGenderConfirmation(false);
    setPendingGenderChange('');
  };

  // Handle profile information updates
  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const { error } = await supabase
        .from('players')
        .update({
          first_name: profileData.first_name,
          last_name: profileData.last_name,
          gender: profileData.gender,
          updated_at: new Date().toISOString()
        })
        .eq('id', currentUser.id);

      if (error) throw error;

      setSuccess('Profile updated successfully!');
      
      // Update the current user in the parent component
      if (onUserUpdate) {
        onUserUpdate({
          ...currentUser,
          first_name: profileData.first_name,
          last_name: profileData.last_name,
          gender: profileData.gender
        });
      }
    } catch (err) {
      console.error('Error updating profile:', err);
      setError(err.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };


  // Handle password reset
  const handlePasswordReset = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await resetPassword(currentUser.email);
      setSuccess(PASSWORD_RESET_EMAIL_SENT_MESSAGE);
      setShowPasswordResetModal(false);
    } catch (err) {
      console.error('Error sending password reset:', err);
      setError(err.message || 'Failed to send password reset email');
    } finally {
      setLoading(false);
    }
  };

  // Handle email change
  const handleEmailChange = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(newEmail)) {
        throw new Error('Please enter a valid email address');
      }

      if (newEmail === currentUser.email) {
        throw new Error('New email must be different from current email');
      }

      // Update email in Supabase Auth - this will send verification to the new email
      const { error } = await supabase.auth.updateUser({
        email: newEmail
      });

      if (error) throw error;

      setSuccess('Email change verification sent! Please check your new email address and click the verification link to confirm the change.');
      setNewEmail('');
      setShowEmailChangeModal(false);
    } catch (err) {
      console.error('Error changing email:', err);
      setError(err.message || 'Failed to send email change verification');
    } finally {
      setLoading(false);
    }
  };

  // Avatar upload handlers
  const handleAvatarFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setAvatarError(null);
    setAvatarSuccess(null);

    if (file.size > AVATAR_MAX_BYTES) {
      setAvatarError(`File is too large. Maximum size is ${AVATAR_MAX_BYTES / (1024 * 1024)}MB.`);
      event.target.value = '';
      return;
    }

    const type = (file.type || '').toLowerCase();
    if (!['image/jpeg', 'image/jpg', 'image/png', 'image/webp'].includes(type)) {
      setAvatarError('Invalid file type. Use JPG, PNG or WebP.');
      event.target.value = '';
      return;
    }

    setSelectedAvatarFile(file);
    setAvatarCropImageUrl(null);
    setAvatarCropModalOpen(true);
    event.target.value = '';
  };

  const handleAvatarCropClick = async () => {
    if (!currentUser?.avatar_url) return;

    setAvatarError(null);
    setAvatarSuccess(null);

    try {
      const url = getPlayerAvatarPublicUrl(currentUser.avatar_url, false);
      const res = await fetch(url);
      if (!res.ok) throw new Error('Failed to load avatar image');
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      setSelectedAvatarFile(null);
      setAvatarCropImageUrl(objectUrl);
      setAvatarCropModalOpen(true);
    } catch (err) {
      console.error('Error loading avatar for crop:', err);
      setAvatarError(err.message || 'Failed to load avatar for cropping');
    }
  };

  const handleAvatarCropConfirm = async (croppedBlob) => {
    if (!currentUser?.id) return;

    setAvatarError(null);
    setAvatarSuccess(null);
    setUploadingAvatar(true);

    try {
      const file = new File([croppedBlob], 'avatar.jpg', { type: 'image/jpeg' });
      const result = await uploadPlayerAvatar(currentUser.id, file, {
        resize: true,
        maxDimension: 400,
        quality: 0.9
      });

      if (!result.success) {
        throw new Error(result.error || 'Failed to upload avatar');
      }

      setAvatarSuccess('Avatar uploaded successfully!');
      setSelectedAvatarFile(null);
      if (avatarCropImageUrl) {
        URL.revokeObjectURL(avatarCropImageUrl);
      }
      setAvatarCropImageUrl(null);
      setAvatarCropModalOpen(false);

      // Refresh user data to show new avatar
      if (onUserUpdate) {
        const { data: updatedPlayer } = await supabase
          .from('players')
          .select('*')
          .eq('id', currentUser.id)
          .single();
        if (updatedPlayer) {
          onUserUpdate(updatedPlayer);
        }
      }

      setTimeout(() => setAvatarSuccess(null), 3000);
    } catch (err) {
      console.error('Error uploading avatar:', err);
      setAvatarError(err.message || 'Failed to upload avatar');
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleAvatarCropClose = () => {
    if (avatarCropImageUrl) {
      URL.revokeObjectURL(avatarCropImageUrl);
    }
    setAvatarCropImageUrl(null);
    setSelectedAvatarFile(null);
    setAvatarCropModalOpen(false);
  };

  const handleAvatarRemove = async () => {
    if (!currentUser?.avatar_url) return;

    setAvatarError(null);
    setAvatarSuccess(null);
    setRemovingAvatar(true);

    try {
      const result = await removePlayerAvatar(currentUser.id, currentUser.avatar_url);

      if (!result.success) {
        throw new Error(result.error || 'Failed to remove avatar');
      }

      setAvatarSuccess('Avatar removed successfully!');

      // Refresh user data
      if (onUserUpdate) {
        const { data: updatedPlayer } = await supabase
          .from('players')
          .select('*')
          .eq('id', currentUser.id)
          .single();
        if (updatedPlayer) {
          onUserUpdate(updatedPlayer);
        }
      }

      setTimeout(() => setAvatarSuccess(null), 3000);
    } catch (err) {
      console.error('Error removing avatar:', err);
      setAvatarError(err.message || 'Failed to remove avatar');
    } finally {
      setRemovingAvatar(false);
    }
  };


  if (!currentUser) {
    return (
      <div className="container mx-auto px-6 py-8 max-w-4xl">
        <Card variant="default" size="large" className="text-center">
          <Heading level={2} className="mb-4">Please Log In</Heading>
          <Text className="mb-8">You need to be logged in to access account settings.</Text>
        </Card>
      </div>
    );
  }

  return (
    <div className={embedded ? '' : 'container mx-auto px-6 py-8 max-w-4xl'}>
      {/* Back Button and header are handled by PlayerProfilePage when embedded */}
      {!embedded && (
        <>
          <div className="mb-8">
            <BackButton onClick={() => navigate('/')}>
              Back to Home
            </BackButton>
          </div>
          
          <div className="mb-8">
            <Heading level={1} className="text-display text-text-primary mb-2">Account Settings</Heading>
            <Text className="text-text-secondary">
              Manage your account information and security settings
            </Text>
          </div>
        </>
      )}

      {/* Error/Success Messages */}
      {error && (
        <Card variant="danger" size="medium" className="mb-6">
          <Typography variant="danger">{error}</Typography>
        </Card>
      )}

      {success && (
        <Card variant="success" size="medium" className="mb-6">
          <Typography variant="success">{success}</Typography>
        </Card>
      )}

      {/* Profile Information Section */}
      <Card variant="default" size="large" className="mb-8">
        <div className="p-6">
          <Heading level={3} className="mb-6 flex items-center space-x-2">
            <span className="text-primary text-lg">👤</span>
            <span>Profile Information</span>
          </Heading>

          <form onSubmit={handleProfileUpdate} className="space-y-6">
            {/* Avatar Upload Section */}
            <div id="profile-picture" className="pb-6 border-b border-border-medium scroll-mt-24">
              <Heading level={4} className="text-text-primary mb-4">Profile Picture</Heading>
              <div className="flex items-start gap-6">
                {/* Avatar Preview */}
                <div className="shrink-0">
                  <div className="w-24 h-24 rounded-full bg-surface-subtle flex items-center justify-center overflow-hidden border-2 border-border-medium">
                    {currentUser?.avatar_url ? (
                      <img
                        src={getPlayerAvatarPublicUrl(currentUser.avatar_url)}
                        alt="Profile avatar"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="text-3xl text-text-muted">
                        {currentUser?.first_name?.charAt(0)?.toUpperCase() || '?'}
                      </span>
                    )}
                  </div>
                </div>

                {/* Avatar Actions */}
                <div className="flex-1">
                  <div className="flex gap-3 items-center flex-wrap mb-3">
                    <div>
                      <input
                        type="file"
                        id="avatar-upload"
                        accept={AVATAR_ACCEPT}
                        onChange={handleAvatarFileSelect}
                        disabled={uploadingAvatar}
                        className="hidden"
                      />
                      <label htmlFor="avatar-upload">
                        <Button
                          variant="secondary"
                          size="small"
                          disabled={uploadingAvatar}
                          onClick={() => document.getElementById('avatar-upload')?.click()}
                        >
                          {uploadingAvatar ? 'Uploading...' : 'Upload Photo'}
                        </Button>
                      </label>
                    </div>

                    {currentUser?.avatar_url && (
                      <>
                        <Button
                          variant="secondary"
                          size="small"
                          onClick={handleAvatarCropClick}
                          disabled={uploadingAvatar}
                        >
                          Crop Photo
                        </Button>
                        <Button
                          variant="danger"
                          size="small"
                          onClick={handleAvatarRemove}
                          disabled={removingAvatar}
                        >
                          {removingAvatar ? 'Removing...' : 'Remove'}
                        </Button>
                      </>
                    )}
                  </div>

                  <Text className="text-xs text-text-muted">
                    Square image recommended. Max size: 5MB. Formats: JPG, PNG, WebP
                  </Text>

                  {/* Avatar Messages */}
                  {avatarSuccess && (
                    <div className="bg-success-light border border-success/40 rounded-md p-2 mt-2">
                      <Text className="text-sm text-success-dark">{avatarSuccess}</Text>
                    </div>
                  )}
                  {avatarError && (
                    <div className="bg-danger-light border border-danger/30 rounded-md p-2 mt-2">
                      <Text className="text-sm text-danger-dark">{avatarError}</Text>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <FormSection>
              <FormGrid columns={2}>
                <FormField>
                  <Input
                    label="First Name"
                    type="text"
                    value={profileData.first_name}
                    onChange={(e) => setProfileData({ ...profileData, first_name: e.target.value })}
                    required
                  />
                </FormField>
                <FormField>
                  <Input
                    label="Last Name"
                    type="text"
                    value={profileData.last_name}
                    onChange={(e) => setProfileData({ ...profileData, last_name: e.target.value })}
                  />
                </FormField>
              </FormGrid>

              <FormField>
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1">
                    Competition category
                  </label>
                  <select
                    value={profileData.gender}
                    onChange={(e) => handleGenderChange(e.target.value)}
                    className="w-full px-3 py-2 border border-border-medium rounded-md shadow-sm bg-surface text-text-primary focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                    required
                  >
                    <option value="">Select category</option>
                    <option value="male">{COMPETITION_CATEGORY_LABELS.male}</option>
                    <option value="female">{COMPETITION_CATEGORY_LABELS.female}</option>
                  </select>
                  <Text className="text-xs text-text-muted mt-1.5 leading-relaxed">
                    {`Used for matchmaking, draws, and Elo ratings.`}
                  </Text>
                </div>
                {showGenderWarning && (
                  <Card variant="warning" size="small" className="mt-2">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-warning-dark">
                          Competition category change
                        </h3>
                        <div className="mt-2 text-sm text-warning-dark">
                          <p>
                            Changing competition category affects historical game categorisation,
                            ELO splits (men&apos;s vs women&apos;s ladders), mixed vs levels grouping, and partnership
                            stats. This is hard to undo—confirm it matches how you compete in events.
                          </p>
                        </div>
                        <div className="mt-3">
                          <div className="-mx-2 -my-1.5 flex">
                            <button
                              type="button"
                              onClick={() => setShowGenderWarning(false)}
                              className="bg-warning-light px-2 py-1.5 rounded-md text-sm font-medium text-warning-dark hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-warning"
                            >
                              I understand
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Card>
                )}
              </FormField>
            </FormSection>

            <div className="flex justify-end">
              <Button
                type="submit"
                variant="primary"
                size="medium"
                disabled={loading}
              >
                {loading ? 'Updating...' : 'Update Profile'}
              </Button>
            </div>
          </form>
        </div>
      </Card>

      {/* Email Settings Section */}
      <Card variant="default" size="large" className="mb-8">
        <div className="p-6">
          <Heading level={3} className="mb-6 flex items-center space-x-2">
            <span className="text-success text-lg">📧</span>
            <span>Email Settings</span>
          </Heading>

          <div className="space-y-6">
            <div className="flex items-center justify-between p-4 bg-surface-subtle rounded-lg border border-border">
              <div>
                <Typography variant="label" className="text-text-primary">Email Address</Typography>
                <Text className="text-text-secondary text-sm">{profileData.email}</Text>
                <Text className="text-text-muted text-xs mt-1">Changing your email will require verification</Text>
              </div>
              <Button
                onClick={() => setShowEmailChangeModal(true)}
                variant="outline"
                size="small"
              >
                Change Email
              </Button>
            </div>
          </div>
        </div>
      </Card>

      {/* Security Settings Section */}
      <Card variant="default" size="large" className="mb-8">
        <div className="p-6">
          <Heading level={3} className="mb-6 flex items-center space-x-2">
            <span className="text-danger text-lg">🔒</span>
            <span>Security Settings</span>
          </Heading>

          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-surface-subtle rounded-lg border border-border">
              <div>
                <Typography variant="label" className="text-text-primary">Password</Typography>
                <Text className="text-text-secondary text-sm">Reset via email link</Text>
              </div>
              <Button
                onClick={() => setShowPasswordResetModal(true)}
                variant="outline"
                size="small"
              >
                Reset Password
              </Button>
            </div>

            <div className="flex items-center justify-between p-4 bg-surface-subtle rounded-lg border border-border">
              <div>
                <Typography variant="label" className="text-text-primary">Account Status</Typography>
                <Text className="text-text-secondary text-sm">
                  {currentUser.is_active ? 'Active' : 'Inactive'}
                </Text>
              </div>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                currentUser.is_active 
                  ? 'bg-success-light text-success-dark' 
                  : 'bg-danger-light text-danger-dark'
              }`}>
                {currentUser.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>
        </div>
      </Card>



      {/* Password Reset Confirmation Modal */}
      <ConfirmationModal
        isOpen={showPasswordResetModal}
        onClose={() => setShowPasswordResetModal(false)}
        onConfirm={handlePasswordReset}
        title="Reset Password"
        message={
          <div className="space-y-3">
            <p className="text-text-secondary">
              We'll send a password reset link to your email address:
            </p>
            <Card variant="info" size="small">
              <p className="text-sm font-medium">{currentUser?.email}</p>
            </Card>
            <p className="text-text-muted text-sm">
              Check your inbox and spam folder. The link lasts 1 hour. If you use a private or incognito window, open the email link in that same window.
            </p>
          </div>
        }
        confirmText="Send Reset Email"
        cancelText="Cancel"
        isDangerous={false}
      />

      {/* Email Change Modal */}
      <Modal
        isOpen={showEmailChangeModal}
        onClose={() => {
          setShowEmailChangeModal(false);
          setNewEmail('');
          setError(null);
          setSuccess(null);
        }}
      >
        <ModalContent size="small" constrainHeight={false}>
            {/* Header */}
            <div className="flex items-center mb-6">
              <div className="flex-shrink-0 mr-3">
                <div className="w-10 h-10 bg-success-light rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
              </div>
              <div>
                <h3 className="text-lg font-medium text-text-primary">Change Email Address</h3>
                <p className="text-sm text-text-muted">Enter your new email address below</p>
              </div>
            </div>

            {/* Form */}
            <form onSubmit={(e) => { e.preventDefault(); handleEmailChange(); }} className="space-y-4">
              <FormField>
                <Input
                  label="Current Email"
                  type="email"
                  value={currentUser?.email || ''}
                  disabled
                  className="bg-surface-subtle"
                />
              </FormField>
              
              <FormField>
                <Input
                  label="New Email Address"
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  required
                  placeholder="Enter your new email address"
                  helperText="A verification email will be sent to the new address"
                />
              </FormField>

              {/* Error/Success Messages */}
              {error && (
                <Card variant="danger" size="small">
                  <p className="text-sm">{error}</p>
                </Card>
              )}
              
              {success && (
                <Card variant="success" size="small">
                  <p className="text-sm">{success}</p>
                </Card>
              )}

              {/* Actions */}
              <div className="flex justify-end space-x-3 pt-4">
                <Button
                  type="button"
                  onClick={() => {
                    setShowEmailChangeModal(false);
                    setNewEmail('');
                    setError(null);
                    setSuccess(null);
                  }}
                  variant="secondary"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  variant="primary"
                >
                  {loading ? 'Sending...' : 'Send Verification'}
                </Button>
              </div>
            </form>
        </ModalContent>
      </Modal>

      {/* Competition category change confirmation Modal */}
      <ConfirmationModal
        isOpen={showGenderConfirmation}
        onClose={cancelGenderChange}
        onConfirm={confirmGenderChange}
        title="Confirm competition category change"
        message={
          <div className="space-y-3">
            <p className="text-text-secondary">
              You are about to change competition category from{' '}
              <strong>
                {COMPETITION_CATEGORY_LABELS[originalGender] ?? originalGender}
              </strong>{' '}
              to{' '}
              <strong>
                {COMPETITION_CATEGORY_LABELS[pendingGenderChange] ?? pendingGenderChange}
              </strong>
              .
            </p>
            <div className="p-4 bg-danger-light border border-danger/30 rounded-md">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-danger" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-danger-dark">
                    ⚠️ This change will significantly impact your account
                  </h3>
                  <div className="mt-2 text-sm text-danger-dark">
                    <ul className="list-disc list-inside space-y-1">
                      <li>All historical game data will be affected</li>
                      <li>ELO ratings and matchmaking will be recalculated</li>
                      <li>Partnership statistics will be updated</li>
                      <li>This change cannot be easily undone</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
            <p className="text-text-muted text-sm">
              Are you absolutely sure you want to proceed with this change?
            </p>
          </div>
        }
        confirmText="Yes, change competition category"
        cancelText="Cancel"
        isDangerous={true}
      />

      {/* Avatar Crop Modal */}
      <ImageCropModal
        isOpen={avatarCropModalOpen}
        onClose={handleAvatarCropClose}
        image={selectedAvatarFile || avatarCropImageUrl}
        onConfirm={handleAvatarCropConfirm}
        aspect={1} // 1:1 aspect ratio for avatars
        cropShape="round" // Circular crop for avatars
        title="Crop profile photo"
        confirmLabel="Use this photo"
        outputOptions={{ format: 'image/jpeg', quality: 0.9, maxWidth: 400 }}
      />

    </div>
  );
};

export default AccountSettings;
